import { HalvardBoard, type CameraView } from '@halvard/board3d';
import { countCliffs, newSeededGame } from '@halvard/rules';
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { parseSeed } from '../game/mountain';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useReducedMotion } from '../hooks/useReducedMotion';
import './Page.css';

/** `/m/:seed`: look at a mountain before playing it. */
export function MountainPreview() {
  const { seed: text } = useParams();
  const [params] = useSearchParams();
  const tall = params.get('tall') === '1';
  const seed = parseSeed(text);
  const game = useMemo(
    () => (seed === null ? null : newSeededGame(seed, tall ? { maxHeight: 6 } : {})),
    [seed, tall],
  );
  const [view, setView] = useState<CameraView>('ivory');
  const [cursor, setCursor] = useState({ r: 8, c: 4 });
  const reducedMotion = useReducedMotion();
  useDocumentTitle(seed === null ? 'Unknown mountain' : `Mountain #${seed}`);

  if (seed === null || !game) {
    return (
      <div className="page-page">
        <h1>No such mountain</h1>
        <p>Mountain numbers are whole numbers from 0 to 4294967295.</p>
        <Link to="/" className="btn">
          Back home
        </Link>
      </div>
    );
  }

  return (
    <div className="page-preview">
      <div className="page-previewBar">
        <div>
          <h1>
            {tall ? 'Tall mountain' : 'Mountain'} #{seed}
          </h1>
          <p className="label">{countCliffs(game.heights)} cliffs</p>
        </div>
        <div className="segmented" role="group" aria-label="Camera view">
          {(['ivory', 'basalt', 'top'] as const).map((v) => (
            <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}>
              {v === 'top' ? 'Top' : v === 'ivory' ? 'Ivory side' : 'Basalt side'}
            </button>
          ))}
        </div>
        <Link to={`/mountain?seed=${seed}${tall ? '&tall=1' : ''}`} className="btn btn-primary">
          Play this mountain
        </Link>
      </div>
      <div className="page-previewBoard">
        <HalvardBoard
          heights={game.heights}
          pieces={game.pieces}
          ply={0}
          cursor={cursor}
          onCursorChange={setCursor}
          view={view}
          facing={view === 'basalt' ? 'B' : 'I'}
          reducedMotion={reducedMotion}
          label={`Mountain #${seed}, starting position`}
        />
      </div>
    </div>
  );
}
